/**
 * networkUtils.js — Shared utility functions for supply chain analytics views.
 */

// Volume / learning-curve exponent. A part bought at higher annual volume is
// expected to cost less per unit; benchmarks and cross-site comparisons scale by
// (reference_volume / volume)^VOLUME_EXP so we never penalize a part for simply
// being low-volume. 0.4 ≈ a moderate volume-discount curve.
export const VOLUME_EXP = 0.4

export function formatEur(val) {
  if (val == null || val === 0) return '€0'
  if (val >= 1e6) return `€${(val / 1e6).toFixed(1)}M`
  if (val >= 1e3) return `€${(val / 1e3).toFixed(0)}K`
  return `€${val.toFixed(0)}`
}

export function formatEurFull(val) {
  if (val == null) return '€0'
  return `€${val.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`
}

export function savingsRange(raw, multiplier) {
  if (multiplier != null) return { lo: raw * multiplier, hi: raw * multiplier }
  return { lo: raw * 0.5, hi: raw * 0.8 }
}

export function formatRange(raw, multiplier) {
  const r = savingsRange(raw, multiplier)
  if (multiplier != null) return formatEur(r.lo)
  return `${formatEur(r.lo)} – ${formatEur(r.hi)}`
}

/**
 * Compute price position for a supplier entry within a cohort.
 * Formula: benchmark = cohort_median_UC × (cohort_median_EAU / part_EAU)^0.4
 * price_position = actual_unit_cost / benchmark
 */
// Cohort axes used for price position grouping (most to least specific)
const COHORT_AXES = ['method', 'material', 'size', 'tolerance_band', 'complexity_tier']

/** Check if a value represents an unknown/unspecified attribute */
function isUnknownValue(val) {
  if (!val) return true
  const lower = (val.toLowerCase ? val.toLowerCase() : String(val)).trim()
  if (!lower) return true
  if (lower === 'unknown' || lower === 'unassigned' || lower === 'n/a' || lower === 'none') return true
  if (lower.endsWith('_unspec') || lower.endsWith('unspec')) return true
  return false
}

/** Methods that produce cohorts too heterogeneous for price comparison */
const EXCLUDED_METHODS = new Set(['assembly'])

/** Translate raw cohort segment abbreviations into human-readable labels */
const COHORT_LABELS = {
  // Materials
  ti: 'Titanium', al: 'Aluminium', ss: 'Stainless Steel', cs: 'Carbon Steel',
  cu: 'Copper', ni: 'Nickel alloy', co: 'Cobalt alloy', w: 'Tungsten',
  br: 'Brass', bz: 'Bronze', mg: 'Magnesium', zn: 'Zinc',
  inconel: 'Inconel (Ni superalloy)', hastelloy: 'Hastelloy (Ni alloy)',
  peek: 'PEEK (polymer)', ptfe: 'PTFE', pom: 'POM (Delrin)',
  // Size envelope
  xs: 'Extra-small envelope', s: 'Small envelope', m: 'Medium envelope',
  l: 'Large envelope', xl: 'Extra-large envelope', xxl: 'Very large envelope',
  // Tolerance band
  ultra: 'Ultra-tight tolerance', tight: 'Tight tolerance',
  standard: 'Standard tolerance', loose: 'Loose tolerance',
  // Complexity
  low: 'Low complexity', medium: 'Medium complexity',
  high: 'High complexity', very_high: 'Very high complexity',
}

function humanizeCohortSegment(segment) {
  const trimmed = (segment || '').trim()
  const key = trimmed.toLowerCase().replace(/[\s-]+/g, '_')
  return COHORT_LABELS[key] || trimmed
}

/** Convert a raw cohort name (pipe-delimited or __-delimited) to readable form */
export function humanizeCohortName(raw) {
  if (!raw) return '\u2014'
  return raw.split(/\s*\|\s*|__/).map(humanizeCohortSegment).join(', ')
}

/**
 * Compute similarity score between two parts using the same logic as the backend.
 * Scoring: size_band/size (+4), material (+3), process/method (+3),
 *          tolerance_band (+2), complexity_tier (+2), envelope/shape (+2),
 *          machining_route (+1), dimensional proximity (+3) = max 20
 * Returns { score, pct, reasons: [{field, value, weight}], diffs: [{field, a, b}] }
 */
export function computeSimilarity(refPart, candidate) {
  const MAX_SCORE = 20
  let score = 0
  const reasons = []
  const diffs = []

  // Size / size_band (+4)
  const refSize = refPart.size || refPart.size_band || ''
  const candSize = candidate.size || candidate.size_band || ''
  if (refSize && candSize && refSize.toLowerCase() === candSize.toLowerCase()) {
    score += 4; reasons.push({ field: 'size', value: refSize, weight: 4 })
  } else if (refSize || candSize) {
    diffs.push({ field: 'size', a: refSize || '\u2014', b: candSize || '\u2014' })
  }

  // Material (+3)
  const refMat = refPart.material || refPart.material_family || ''
  const candMat = candidate.material || candidate.material_family || ''
  if (refMat && candMat && refMat.toLowerCase() === candMat.toLowerCase()) {
    score += 3; reasons.push({ field: 'material', value: refMat, weight: 3 })
  } else if (refMat || candMat) {
    diffs.push({ field: 'material', a: refMat || '\u2014', b: candMat || '\u2014' })
  }

  // Process / method (+3)
  const refProc = refPart.method || refPart.process_family || refPart.primary_process || ''
  const candProc = candidate.method || candidate.process_family || candidate.primary_process || ''
  if (refProc && candProc && refProc.toLowerCase() === candProc.toLowerCase()) {
    score += 3; reasons.push({ field: 'process', value: refProc, weight: 3 })
  } else if (refProc || candProc) {
    diffs.push({ field: 'process', a: refProc || '\u2014', b: candProc || '\u2014' })
  }

  // Tolerance band (+2)
  const refTol = refPart.tolerance_band || ''
  const candTol = candidate.tolerance_band || ''
  if (refTol && candTol && !isUnknownValue(refTol) && refTol.toLowerCase() === candTol.toLowerCase()) {
    score += 2; reasons.push({ field: 'tolerance', value: refTol, weight: 2 })
  } else if (refTol && candTol && !isUnknownValue(refTol) && !isUnknownValue(candTol) && refTol !== candTol) {
    diffs.push({ field: 'tolerance', a: refTol, b: candTol })
  }

  // Complexity tier (+2)
  const refComp = refPart.complexity_tier || ''
  const candComp = candidate.complexity_tier || ''
  if (refComp && candComp && !isUnknownValue(refComp) && refComp.toLowerCase() === candComp.toLowerCase()) {
    score += 2; reasons.push({ field: 'complexity', value: refComp, weight: 2 })
  } else if (refComp && candComp && !isUnknownValue(refComp) && !isUnknownValue(candComp) && refComp !== candComp) {
    diffs.push({ field: 'complexity', a: refComp, b: candComp })
  }

  // Envelope / shape (+2)
  const refEnv = refPart.envelope_class || refPart.shape_class || ''
  const candEnv = candidate.envelope_class || candidate.shape_class || ''
  if (refEnv && candEnv && !isUnknownValue(refEnv) && refEnv.toLowerCase() === candEnv.toLowerCase()) {
    score += 2; reasons.push({ field: 'envelope', value: refEnv, weight: 2 })
  } else if (refEnv && candEnv && !isUnknownValue(refEnv) && !isUnknownValue(candEnv) && refEnv !== candEnv) {
    diffs.push({ field: 'envelope', a: refEnv, b: candEnv })
  }

  // Machining route (+1)
  const refRoute = refPart.machining_route || ''
  const candRoute = candidate.machining_route || ''
  if (refRoute && candRoute && refRoute.toLowerCase() === candRoute.toLowerCase()) {
    score += 1; reasons.push({ field: 'route', value: refRoute, weight: 1 })
  }

  // Dimensional proximity (+3) — based on tightest_mm as a proxy
  if (refPart.tightest_mm != null && candidate.tightest_mm != null) {
    const ref = Number(refPart.tightest_mm)
    const cand = Number(candidate.tightest_mm)
    if (ref > 0) {
      const ratio = Math.abs(ref - cand) / ref
      if (ratio <= 0.10) { score += 3; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 3 }) }
      else if (ratio <= 0.25) { score += 2; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 2 }) }
      else if (ratio <= 0.50) { score += 1; reasons.push({ field: 'tolerance_value', value: `within ${Math.round(ratio * 100)}%`, weight: 1 }) }
      else { diffs.push({ field: 'tolerance_value', a: ref.toFixed(3) + 'mm', b: cand.toFixed(3) + 'mm' }) }
    }
  }

  return { score, pct: Math.round(score / MAX_SCORE * 100), reasons, diffs }
}

/**
 * Build a cohort key from a part's known attributes only.
 * Skips any axis where the value is unknown/unspecified.
 * Returns null if fewer than MIN_COHORT_ATTRS known attributes exist.
 */
const MIN_COHORT_ATTRS = 2

function buildCohortKey(p) {
  // Exclude parts whose method produces heterogeneous cohorts
  const method = p.method
  if (method && EXCLUDED_METHODS.has(method.toLowerCase().trim())) return null
  const knownParts = []
  for (const ax of COHORT_AXES) {
    const val = p[ax]
    if (!isUnknownValue(val)) {
      knownParts.push(val)
    }
  }
  return knownParts.length >= MIN_COHORT_ATTRS ? knownParts.join(' | ') : null
}

/** Count number of known cohort attributes on a part */
export function cohortAttrCount(p) {
  let count = 0
  for (const ax of COHORT_AXES) {
    if (!isUnknownValue(p[ax])) count++
  }
  return count
}

export function computePricePositions(parts) {
  // Group by cohort built from known attributes
  const cohorts = {}
  parts.forEach(p => {
    const coh = buildCohortKey(p)
    if (!coh) return // no known attributes — cannot benchmark
    if (!cohorts[coh]) cohorts[coh] = []
    p.suppliers.forEach(s => {
      if (s.unit_cost > 0) {
        cohorts[coh].push({
          unit_cost: s.unit_cost,
          eau: parseFloat(s.eau) || 0,
          supplier: s.name,
          pn: p.pn || p.filename || '',
          description: p.description || '',
          file_id: p.file_id || null,
          material: p.material || '',
          method: p.method || '',
          setup_count: p.setup_count || null,
          dim_count: p.dim_count || null,
        })
      }
    })
  })

  // Compute cohort medians
  const cohortStats = {}
  Object.entries(cohorts).forEach(([coh, entries]) => {
    if (entries.length < 3) return
    const costs = entries.map(e => e.unit_cost).sort((a, b) => a - b)
    const eaus = entries.filter(e => e.eau > 0).map(e => e.eau).sort((a, b) => a - b)
    cohortStats[coh] = {
      median_uc: costs[Math.floor(costs.length / 2)],
      median_eau: eaus.length > 0 ? eaus[Math.floor(eaus.length / 2)] : 0,
      entries,
    }
  })

  // Assign price positions
  return parts.map(p => {
    const coh = buildCohortKey(p)
    if (!coh) return p // no cohort — cannot benchmark
    const stats = cohortStats[coh]
    if (!stats) return p

    const attrCount = cohortAttrCount(p)

    const enrichedSuppliers = p.suppliers.map(s => {
      if (!s.unit_cost || s.unit_cost <= 0) return s
      const eau = parseFloat(s.eau) || 0
      if (eau <= 0 || stats.median_eau <= 0) return s

      const benchmark = stats.median_uc * Math.pow(stats.median_eau / eau, VOLUME_EXP)
      const pp = s.unit_cost / benchmark

      return {
        ...s,
        price_position: Math.round(pp * 100) / 100,
        benchmark: Math.round(benchmark * 100) / 100,
        cohort_median_uc: Math.round(stats.median_uc * 100) / 100,
        cohort_median_eau: Math.round(stats.median_eau),
        cohort_size: stats.entries.length,
        cohort_entries: stats.entries,
        cohort_name: coh,
        cohort_attr_count: attrCount,
      }
    })

    return { ...p, suppliers: enrichedSuppliers, cohort_attr_count: attrCount }
  })
}

// ─── Negotiation Strategy Scoring ─────────────────────────────────────────────
// Scores 6 classical procurement strategies for a given negotiation opportunity.
// Each strategy scores 0-100. Returns sorted array, highest first.

const NEGO_STRATEGIES = [
  { id: 'benchmarking', name: 'Benchmarking / Should-Cost', icon: '\u{1F4CA}',
    desc: 'Present market data showing the supplier is above benchmark. Data-driven, low conflict.' },
  { id: 'volume_bundling', name: 'Volume Bundling', icon: '\u{1F4E6}',
    desc: 'Consolidate all affected parts into a single negotiation package. Reward alignment with commitment.' },
  { id: 'dual_sourcing', name: 'Dual-Sourcing', icon: '\u2696\uFE0F',
    desc: 'Qualify a second source for high-spend items. Structural leverage for future negotiations.' },
  { id: 'partnership_lta', name: 'Partnership / LTA', icon: '\u{1F91D}',
    desc: 'Offer long-term volume commitment in exchange for pricing concession. Best when relationship is valuable.' },
  { id: 'spec_optimization', name: 'Specification Optimization', icon: '\u2699\uFE0F',
    desc: 'Review tolerances, materials, and complexity. Reduce cost drivers rather than squeezing margin.' },
]

export function scoreNegotiationStrategies(g, goodItems, parts) {
  const items = g.items // array of { part, supplier, overspend }
  const itemCount = items.length
  const avgPP = items.reduce((s, i) => s + i.supplier.price_position, 0) / itemCount
  const totalSpend = g.totalSpend

  // Compute signals from the data
  // 1. Cohort richness: avg cohort size across items (more entries = more market data)
  const cohortSizes = items.map(i => i.supplier.cohort_size || 0).filter(c => c > 0)
  const avgCohortSize = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0

  // 2. Alternative suppliers: count distinct other suppliers seen in cohort entries
  const altSuppliers = new Set()
  items.forEach(i => {
    (i.supplier.cohort_entries || []).forEach(e => {
      if (e.supplier && e.supplier !== g.supplier) altSuppliers.add(e.supplier)
    })
  })
  const altCount = altSuppliers.size

  // 3. Sole-source ratio: % of parts where this is the only priced supplier
  const soleSourced = items.filter(i => {
    const pricedSuppliers = i.part.suppliers.filter(s => s.unit_cost > 0)
    return pricedSuppliers.length === 1
  }).length
  const soleRatio = soleSourced / itemCount

  // 4. Part complexity: ratio of parts with tight tolerances or high complexity
  const complexParts = items.filter(i => {
    const tol = (i.part.tolerance_band || '').toLowerCase()
    const comp = (i.part.complexity_tier || '').toLowerCase()
    return tol.includes('tight') || comp.includes('high') || comp.includes('very')
  }).length
  const complexRatio = complexParts / itemCount

  // 5. Competitive items from same supplier
  const goodCount = goodItems.length
  const goodSpend = goodItems.reduce((s, gi) => s + gi.spend, 0)

  // 6. Spend concentration
  const spendHigh = totalSpend > 100000
  const spendVeryHigh = totalSpend > 500000

  // ─── Score each strategy ───
  const scores = []

  // BENCHMARKING: strong when good data exists + supplier has competitive items
  let benchScore = 30 // baseline: we always have benchmarks
  const benchFactors = [{ signal: 'Baseline (benchmarks exist)', pts: 30 }]
  if (avgCohortSize >= 5) { benchScore += 20; benchFactors.push({ signal: `Large cohorts (avg ${avgCohortSize.toFixed(1)} members)`, pts: 20 }) }
  else if (avgCohortSize >= 3) { benchScore += 10; benchFactors.push({ signal: `Moderate cohorts (avg ${avgCohortSize.toFixed(1)} members)`, pts: 10 }) }
  if (goodCount > 0) { benchScore += 20; benchFactors.push({ signal: `${goodCount} competitive items prove supplier CAN price fairly`, pts: 20 }) }
  if (avgPP >= 1.3 && avgPP <= 2.0) { benchScore += 15; benchFactors.push({ signal: `Credible range (${avgPP.toFixed(2)}x avg) — not absurd`, pts: 15 }) }
  if (avgPP > 2.0) { benchScore += 5; benchFactors.push({ signal: `Extreme gap (${avgPP.toFixed(2)}x) — data useful but may be dismissed`, pts: 5 }) }
  if (altCount >= 3) { benchScore += 10; benchFactors.push({ signal: `${altCount} alt suppliers in market validate benchmarks`, pts: 10 }) }
  benchScore = Math.min(100, benchScore)

  // VOLUME BUNDLING: strong when many parts, high total spend
  let bundleScore = 10
  const bundleFactors = [{ signal: 'Baseline', pts: 10 }]
  if (itemCount >= 8) { bundleScore += 25; bundleFactors.push({ signal: `Large bundle (${itemCount} parts)`, pts: 25 }) }
  else if (itemCount >= 5) { bundleScore += 18; bundleFactors.push({ signal: `Moderate bundle (${itemCount} parts)`, pts: 18 }) }
  else if (itemCount >= 3) { bundleScore += 10; bundleFactors.push({ signal: `Small bundle (${itemCount} parts)`, pts: 10 }) }
  if (spendVeryHigh) { bundleScore += 25; bundleFactors.push({ signal: `Very high spend (${formatEur(totalSpend)})`, pts: 25 }) }
  else if (spendHigh) { bundleScore += 18; bundleFactors.push({ signal: `High spend (${formatEur(totalSpend)})`, pts: 18 }) }
  else if (totalSpend > 50000) { bundleScore += 10; bundleFactors.push({ signal: `Moderate spend (${formatEur(totalSpend)})`, pts: 10 }) }
  if (goodCount > 0) { bundleScore += 10; bundleFactors.push({ signal: `${goodCount} good items = "bringing ALL the business"`, pts: 10 }) }
  if (avgPP < 1.8) { bundleScore += 10; bundleFactors.push({ signal: `Gap not extreme (${avgPP.toFixed(2)}x) — supplier likely to engage`, pts: 10 }) }
  bundleScore = Math.min(100, bundleScore)

  // DUAL-SOURCING: strong when sole-sourced + some alternates exist
  let dualScore = 5
  const dualFactors = [{ signal: 'Baseline', pts: 5 }]
  if (soleRatio > 0.7) { dualScore += 25; dualFactors.push({ signal: `High sole-source (${Math.round(soleRatio * 100)}% single-sourced)`, pts: 25 }) }
  else if (soleRatio > 0.4) { dualScore += 15; dualFactors.push({ signal: `Moderate sole-source (${Math.round(soleRatio * 100)}%)`, pts: 15 }) }
  if (altCount >= 2) { dualScore += 20; dualFactors.push({ signal: `${altCount} alternate suppliers exist to qualify`, pts: 20 }) }
  else if (altCount >= 1) { dualScore += 10; dualFactors.push({ signal: `${altCount} alternate supplier identified`, pts: 10 }) }
  if (spendHigh) { dualScore += 15; dualFactors.push({ signal: `High spend justifies qualification cost`, pts: 15 }) }
  if (avgPP > 1.5) { dualScore += 15; dualFactors.push({ signal: `Severe gap (${avgPP.toFixed(2)}x) warrants structural change`, pts: 15 }) }
  if (complexRatio > 0.5) { dualScore -= 10; dualFactors.push({ signal: `High complexity (${Math.round(complexRatio * 100)}%) — hard to qualify alternates`, pts: -10 }) }
  dualScore = Math.max(0, Math.min(100, dualScore))

  // PARTNERSHIP / LTA: strong when large spend, moderate overpricing, valuable relationship
  let ltaScore = 10
  const ltaFactors = [{ signal: 'Baseline', pts: 10 }]
  if (spendVeryHigh) { ltaScore += 25; ltaFactors.push({ signal: `Very high spend (${formatEur(totalSpend)}) — worth a deal`, pts: 25 }) }
  else if (spendHigh) { ltaScore += 18; ltaFactors.push({ signal: `High spend (${formatEur(totalSpend)})`, pts: 18 }) }
  else if (totalSpend > 50000) { ltaScore += 10; ltaFactors.push({ signal: `Moderate spend (${formatEur(totalSpend)})`, pts: 10 }) }
  if (goodCount >= 3) { ltaScore += 20; ltaFactors.push({ signal: `${goodCount} competitive items — relationship worth preserving`, pts: 20 }) }
  else if (goodCount > 0) { ltaScore += 10; ltaFactors.push({ signal: `${goodCount} competitive item(s) — some relationship value`, pts: 10 }) }
  if (avgPP < 1.5) { ltaScore += 15; ltaFactors.push({ signal: `Moderate gap (${avgPP.toFixed(2)}x) — partnership can fix`, pts: 15 }) }
  else if (avgPP < 1.8) { ltaScore += 5; ltaFactors.push({ signal: `Reasonable gap (${avgPP.toFixed(2)}x)`, pts: 5 }) }
  if (itemCount >= 5) { ltaScore += 10; ltaFactors.push({ signal: `Deep relationship (${itemCount} parts)`, pts: 10 }) }
  if (soleRatio > 0.5) { ltaScore += 10; ltaFactors.push({ signal: `High dependency (${Math.round(soleRatio * 100)}% sole-sourced) — better to partner`, pts: 10 }) }
  ltaScore = Math.min(100, ltaScore)

  // SPEC OPTIMIZATION: strong when parts are complex/tight tolerance
  let specScore = 5
  const specFactors = [{ signal: 'Baseline', pts: 5 }]
  if (complexRatio > 0.6) { specScore += 30; specFactors.push({ signal: `High complexity ratio (${Math.round(complexRatio * 100)}% complex parts)`, pts: 30 }) }
  else if (complexRatio > 0.3) { specScore += 15; specFactors.push({ signal: `Moderate complexity (${Math.round(complexRatio * 100)}% complex)`, pts: 15 }) }
  // Check for expensive materials
  const expensiveMats = items.filter(i => {
    const mat = (i.part.material || '').toLowerCase()
    return mat.includes('titanium') || mat.includes('inconel') || mat.includes('hastelloy') || mat.includes('nickel') || mat.includes('cobalt') || mat.includes('tungsten')
  }).length
  if (expensiveMats > 0) { specScore += 20; specFactors.push({ signal: `${expensiveMats} parts use exotic materials (Ti, Inconel, etc.)`, pts: 20 }) }
  if (avgPP > 2.0) { specScore += 15; specFactors.push({ signal: `Extreme gap (${avgPP.toFixed(2)}x) may be spec-driven`, pts: 15 }) }
  if (avgPP > 1.5 && complexRatio > 0.3) { specScore += 10; specFactors.push({ signal: `Overpricing + complexity suggests spec review opportunity`, pts: 10 }) }
  // Low confidence if we don't have good attribute data
  const avgAttrCount = items.reduce((s, i) => s + (cohortAttrCount(i.part) || 0), 0) / itemCount
  if (avgAttrCount < 3) { specScore -= 10; specFactors.push({ signal: `Low attribute data (avg ${avgAttrCount.toFixed(1)} attrs) — can't assess specs`, pts: -10 }) }
  specScore = Math.max(0, Math.min(100, specScore))

  // Assemble and sort
  const strategyScores = [
    { ...NEGO_STRATEGIES[0], score: benchScore, factors: benchFactors },
    { ...NEGO_STRATEGIES[1], score: bundleScore, factors: bundleFactors },
    { ...NEGO_STRATEGIES[2], score: dualScore, factors: dualFactors },
    { ...NEGO_STRATEGIES[3], score: ltaScore, factors: ltaFactors },
    { ...NEGO_STRATEGIES[4], score: specScore, factors: specFactors },
  ].sort((a, b) => b.score - a.score)

  return strategyScores
}

/**
 * Detect savings opportunities from parts data.
 * @param {Array} parts - enriched parts with price positions
 * @param {Object} params - tunable parameters
 * @param {number} params.priceThreshold - PP cutoff for negotiation (default 1.3)
 * @param {number} params.minSize - minimum savings in EUR to surface (default 500)
 * @param {number} params.outlierThreshold - ratio above which to flag as data error (default 3)
 * @param {number} params.similarMinAttrs - min shared attributes for similar-part gap (default 4)
 */
export function computeOpportunities(parts, params = {}) {
  const {
    priceThreshold = 1.3,
    minSize = 500,
    outlierThreshold = 3,
    similarMinAttrs = 4,
  } = params

  const opps = []

  // 1. Negotiation targets: PP > priceThreshold, significant spend
  const negoTargets = []
  parts.forEach(p => {
    p.suppliers.forEach(s => {
      if (s.price_position && s.price_position > priceThreshold && s.spend > minSize) {
        const overspend = s.spend * (1 - 1 / s.price_position)
        negoTargets.push({ part: p, supplier: s, overspend })
      }
    })
  })

  // Group by supplier
  const negoBySupplier = {}
  negoTargets.forEach(t => {
    const key = t.supplier.name
    if (!negoBySupplier[key]) negoBySupplier[key] = { supplier: key, items: [], totalOverspend: 0, totalSpend: 0 }
    negoBySupplier[key].items.push(t)
    negoBySupplier[key].totalOverspend += t.overspend
    negoBySupplier[key].totalSpend += t.supplier.spend
  })

  Object.values(negoBySupplier).sort((a, b) => b.totalOverspend - a.totalOverspend).forEach(g => {
    if (g.totalOverspend < minSize) return
    const uniqueParts = [...new Map(g.items.map(i => [i.part.pn, i.part])).values()]
    const avgPP = g.items.reduce((s, i) => s + i.supplier.price_position, 0) / g.items.length

    // Gather competitively-priced items from same supplier (PP ≤ threshold, valid PP)
    const overPNs = new Set(g.items.map(i => i.part.pn))
    const goodItems = []
    parts.forEach(p => {
      if (overPNs.has(p.pn)) return
      p.suppliers.forEach(s => {
        if (s.name === g.supplier && s.price_position && s.price_position <= priceThreshold && s.price_position > 0 && s.spend > 0) {
          goodItems.push({ pn: p.pn, desc: p.description || '', pp: s.price_position, spend: s.spend, uc: Number(s.unit_cost), bm: s.benchmark || 0, eau: parseFloat(s.eau) || 0, file_id: p.file_id || null, filename: p.filename || '', cohort_entries: s.cohort_entries || [], cohort_name: s.cohort_name || '', cohort_size: s.cohort_size || 0, material: p.material || '', method: p.method || '' })
        }
      })
    })
    goodItems.sort((a, b) => a.pp - b.pp)

    // Score negotiation strategies
    const strategies = scoreNegotiationStrategies(g, goodItems, parts)

    opps.push({
      type: 'negotiation',
      title: `${g.supplier} — ${g.items.length} parts above expected price`,
      parts: uniqueParts,
      spendExposed: g.totalSpend,
      estSavings: g.totalOverspend,
      confidence: g.items.length >= 5 ? 'High' : g.items.length >= 2 ? 'Medium' : 'Low',
      avgPP,
      strategies,
      detail: `Average price position ${avgPP.toFixed(2)}x. ${g.items.length} line items priced ${Math.round((avgPP - 1) * 100)}% above cohort benchmark on average.`,
      vizData: {
        supplierName: g.supplier,
        items: g.items.map(i => ({
          pn: i.part.pn, desc: i.part.description || '',
          pp: i.supplier.price_position, spend: i.supplier.spend,
          uc: Number(i.supplier.unit_cost), bm: i.supplier.benchmark,
          eau: parseFloat(i.supplier.eau) || 0,
          file_id: i.part.file_id || null, filename: i.part.filename || '',
          cohort_entries: i.supplier.cohort_entries || [],
          cohort_name: i.supplier.cohort_name || '',
          cohort_size: i.supplier.cohort_size || 0,
          material: i.part.material || '', method: i.part.method || '',
        })).slice(0, 12),
        goodItems: goodItems.slice(0, 8),
      },
    })
  })

  // 2. Sole-source risk
  const soleParts = parts.filter(p => {
    const pricedSuppliers = p.suppliers.filter(s => s.unit_cost > 0)
    return pricedSuppliers.length === 1 && p.total_spend > 2000
  })
  const soleBySupplier = {}
  soleParts.forEach(p => {
    const key = (p.suppliers.find(s => s.unit_cost > 0) || p.suppliers[0]).name
    if (!soleBySupplier[key]) soleBySupplier[key] = { supplier: key, parts: [], totalSpend: 0 }
    soleBySupplier[key].parts.push(p)
    soleBySupplier[key].totalSpend += p.total_spend
  })

  Object.values(soleBySupplier).sort((a, b) => b.totalSpend - a.totalSpend).forEach(g => {
    if (g.totalSpend < 5000) return
    opps.push({
      type: 'sole_source',
      title: `${g.supplier.substring(0, 35)} — ${g.parts.length} sole-sourced parts`,
      parts: g.parts,
      spendExposed: g.totalSpend,
      estSavings: g.totalSpend * 0.12,
      confidence: g.parts.length >= 5 ? 'High' : 'Medium',
      detail: `${formatEur(g.totalSpend)} spend at risk with no alternative supplier. Qualifying a second source could reduce risk and enable competitive pricing (typical 8–15% improvement).`,
      vizData: {
        supplierName: g.supplier, partCount: g.parts.length, totalSpend: g.totalSpend,
        topParts: g.parts.slice().sort((a, b) => b.total_spend - a.total_spend).slice(0, 8)
          .map(p => ({ pn: p.pn, desc: p.description || '', spend: p.total_spend })),
      },
    })
  })

  // 3. Part Family Optimization: groups of similar parts with price spread or supplier fragmentation
  const familyGroups = {}
  parts.forEach(p => {
    // Skip assemblies
    if (p.method && p.method.toLowerCase().includes('assembl')) return
    const attrs = [p.method, p.material, p.size, p.tolerance_band, p.complexity_tier].filter(v => !isUnknownValue(v))
    if (attrs.length < similarMinAttrs) return
    if (!p.suppliers.some(s => s.unit_cost > 0 && s.spend > 0)) return
    const famKey = attrs.join('|')
    if (!familyGroups[famKey]) familyGroups[famKey] = { parts: [], suppliers: new Set(), totalSpend: 0 }
    familyGroups[famKey].parts.push(p)
    familyGroups[famKey].totalSpend += p.total_spend
    p.suppliers.forEach(s => { if (s.name && s.unit_cost > 0) familyGroups[famKey].suppliers.add(s.name) })
  })

  Object.entries(familyGroups).sort((a, b) => b[1].totalSpend - a[1].totalSpend).forEach(([famKey, data]) => {
    const uniquePNs = [...new Set(data.parts.map(p => p.pn))]
    if (uniquePNs.length < 2) return

    // Collect all priced entries (exclude zero-cost lines)
    const allPrices = []
    data.parts.forEach(p => {
      p.suppliers.forEach(s => {
        if (s.unit_cost > 0 && s.spend > 0) {
          allPrices.push({ pn: p.pn, name: s.name, uc: s.unit_cost, spend: s.spend, eau: parseFloat(s.eau) || 0 })
        }
      })
    })
    if (allPrices.length < 2) return

    // Drop outliers (>8x median)
    const ucs = allPrices.map(e => e.uc).sort((a, b) => a - b)
    const med = ucs[Math.floor(ucs.length / 2)]
    const valid = allPrices.filter(e => e.uc <= med * 8 && e.uc >= med / 8)
    if (valid.length < 2) return

    // Only count parts that have valid pricing (not zero-cost or outlier-dropped)
    const pricedPNs = [...new Set(valid.map(e => e.pn))]
    if (pricedPNs.length < 2) return

    const best = valid.reduce((a, b) => a.uc < b.uc ? a : b)
    const worst = valid.reduce((a, b) => a.uc > b.uc ? a : b)
    const spread = worst.uc / best.uc
    const supplierCount = data.suppliers.size

    // Two levers: price alignment + consolidation
    // Price alignment savings (measured)
    let priceSavings = 0
    if (best.pn !== worst.pn && spread >= priceThreshold) {
      valid.filter(e => e.pn !== best.pn).forEach(e => {
        const expected = (best.eau > 0 && e.eau > 0)
          ? best.uc * Math.pow(best.eau / e.eau, VOLUME_EXP)
          : best.uc
        if (e.uc > expected) priceSavings += e.spend * (1 - expected / e.uc)
      })
    }
    // Consolidation savings (estimated 8% from volume leverage)
    const consolSavings = supplierCount >= 4 ? data.totalSpend * 0.08 : 0
    // Take the higher of the two as the primary savings
    const savings = Math.max(priceSavings, consolSavings)
    if (savings < minSize) return

    const hasPriceGap = priceSavings > 0 && spread >= priceThreshold
    const hasConsolidation = supplierCount >= 4
    if (!hasPriceGap && !hasConsolidation) return

    // Build sub-signals for display
    const signals = []
    if (hasPriceGap) signals.push(spread >= 10 ? `${Math.round(spread)}× price range` : `${Math.round((spread - 1) * 100)}% price spread`)
    if (hasConsolidation) signals.push(`${supplierCount} suppliers`)

    const famLabel = famKey.replace(/\|/g, ' + ')

    // Per-supplier spend breakdown
    const supSpend = {}
    data.parts.forEach(p => p.suppliers.forEach(s => { if (s.spend > 0) supSpend[s.name] = (supSpend[s.name] || 0) + s.spend }))
    const supBreakdown = Object.entries(supSpend).sort((a, b) => b[1] - a[1]).map(([name, spend]) => ({ name, spend }))

    // Per-part price summary for viz — only parts with valid pricing
    const partPrices = pricedPNs.map(pn => {
      const entries = valid.filter(e => e.pn === pn)
      const avgUC = entries.length > 0 ? entries.reduce((s, e) => s + e.uc, 0) / entries.length : 0
      const mainSupplier = entries.sort((a, b) => b.spend - a.spend)[0]?.name || ''
      const partObj = data.parts.find(pp => pp.pn === pn)
      return { pn, desc: partObj?.description || '', uc: avgUC, supplier: mainSupplier, spend: entries.reduce((s, e) => s + e.spend, 0) }
    }).filter(p => p.uc > 0).sort((a, b) => b.uc - a.uc)

    // Compute median and parts-above-median for better summaries
    const sortedPP = partPrices.length > 0 ? [...partPrices].sort((a, b) => a.uc - b.uc) : []
    const medUC = sortedPP.length > 0
      ? (sortedPP.length % 2 === 0 ? (sortedPP[sortedPP.length / 2 - 1].uc + sortedPP[sortedPP.length / 2].uc) / 2 : sortedPP[Math.floor(sortedPP.length / 2)].uc)
      : 0
    const aboveMedianParts = sortedPP.filter(p => p.uc > medUC)
    const aboveMedianSpend = aboveMedianParts.reduce((s, p) => s + p.spend, 0)

    opps.push({
      type: 'part_family',
      title: `${famLabel} — ${pricedPNs.length} parts, ${signals.join(', ')}`,
      parts: data.parts,
      spendExposed: data.totalSpend,
      estSavings: savings,
      confidence: pricedPNs.length >= 5 ? 'High' : pricedPNs.length >= 3 ? 'Medium' : 'Low',
      detail: `${pricedPNs.length} engineering-similar parts (${famLabel}) with ${formatEur(data.totalSpend)} total spend.`
        + (hasPriceGap ? ` Prices range \u20ac${best.uc.toFixed(2)}\u2013\u20ac${worst.uc.toFixed(2)} (${Math.round(spread)}\u00D7), median \u20ac${medUC.toFixed(2)}. ${aboveMedianParts.length} parts above median account for ${formatEur(aboveMedianSpend)} spend \u2014 aligning to best-in-family pricing is the primary lever.` : '')
        + (hasConsolidation ? ` Fragmented across ${supplierCount} suppliers \u2014 consolidating to 1\u20132 preferred could unlock additional volume leverage.` : ''),
      ppRatio: spread,
      vizData: {
        famLabel,
        partCount: pricedPNs.length,
        supplierCount,
        totalSpend: data.totalSpend,
        hasPriceGap,
        hasConsolidation,
        spread,
        best: { pn: best.pn, desc: data.parts.find(pp => pp.pn === best.pn)?.description || '', uc: best.uc, supplier: best.name },
        worst: { pn: worst.pn, desc: data.parts.find(pp => pp.pn === worst.pn)?.description || '', uc: worst.uc, supplier: worst.name },
        suppliers: supBreakdown.slice(0, 8),
        partPrices,
      },
    })
  })

  // 4. Quick wins
  parts.forEach(p => {
    const pricedSuppliers = p.suppliers.filter(s => s.unit_cost > 0)
    if (pricedSuppliers.length < 2) return
    const expensive = pricedSuppliers.filter(s => s.price_position && s.price_position > priceThreshold + 0.2)
    const cheaper = pricedSuppliers.filter(s => s.price_position && s.price_position < 1.0)
    if (expensive.length > 0 && cheaper.length > 0) {
      const best = cheaper.sort((a, b) => a.price_position - b.price_position)[0]
      const worst = expensive.sort((a, b) => b.price_position - a.price_position)[0]
      const savings = worst.spend * (1 - best.price_position / worst.price_position)
      if (savings > minSize) {
        // Outlier analysis: flag extreme ratios that likely indicate data issues
        // Grounded on real data: P75=2.8x, P90=6.7x across 2,865 multi-sourced parts
        const ppRatio = worst.price_position / Math.max(best.price_position, 0.01)
        const outlierFlags = []
        if (ppRatio > outlierThreshold * 3) {
          outlierFlags.push('Extreme price ratio (>' + Math.round(ppRatio) + 'x) — almost certainly a data error (e.g. unit-of-measure: box vs. each, or currency mismatch). Verify before acting.')
        } else if (ppRatio > outlierThreshold * 1.5) {
          outlierFlags.push('Very large price gap (' + ppRatio.toFixed(1) + 'x) — likely UoM or quantity-basis difference (e.g. per-piece vs. per-hundred). Confirm with supplier.')
        } else if (ppRatio > outlierThreshold) {
          outlierFlags.push('Unusual price gap (' + ppRatio.toFixed(1) + 'x) — above P75 of typical variation. Validate pricing basis before switching.')
        }
        opps.push({
          type: 'quick_win',
          title: `${p.pn}${p.description ? ' (' + p.description + ')' : ''} — switch from ${worst.name.substring(0, 25)} to ${best.name.substring(0, 25)}`,
          parts: [p],
          spendExposed: worst.spend,
          estSavings: savings,
          confidence: ppRatio > outlierThreshold * 1.5 ? 'Low' : ppRatio > outlierThreshold ? 'Medium' : 'High',
          detail: `${worst.name} charges ${worst.price_position.toFixed(2)}x benchmark while ${best.name} is at ${best.price_position.toFixed(2)}x for the same part. Switching saves ~${formatEur(savings)}/year.`,
          outlierFlags,
          ppRatio,
          vizData: {
            pn: p.pn, desc: p.description || '',
            current: { name: worst.name, pp: worst.price_position, spend: worst.spend },
            alternative: { name: best.name, pp: best.price_position, spend: best.spend },
          },
        })
      }
    }
  })

  // 5. Site price gap — the SAME part bought at different prices across sites.
  // Exposes siloed sourcing. Savings are VOLUME-ADJUSTED: a low-volume site
  // legitimately pays more per unit, so we only count the gap beyond what order
  // volume explains, measured against the best-achieved in-network price.
  parts.forEach(p => {
    let priced = (p.sites || []).filter(s => s.unit_cost > 0 && s.spend > 0)
    if (priced.length < 2) return
    // Drop implausible per-site prices (unit/currency mix-ups, one-off buys) that
    // would otherwise fabricate enormous gaps — keep within 8x of the median.
    const ucs = priced.map(s => s.unit_cost).sort((a, b) => a - b)
    const med = ucs[Math.floor(ucs.length / 2)]
    priced = priced.filter(s => s.unit_cost <= med * 8 && s.unit_cost >= med / 8)
    if (priced.length < 2) return

    const bestSite = priced.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b))
    const best = bestSite.unit_cost
    const bestEau = parseFloat(bestSite.eau) || 0
    const rawSpread = Math.max(...priced.map(s => s.unit_cost)) / best
    if (rawSpread < 1.2) return // <20% raw gap: not worth flagging

    // Volume-adjusted savings: each site is compared to the best price scaled to
    // that site's own volume (best site is the achievable floor). Only the excess
    // over the volume-explained premium counts.
    let savings = 0
    priced.forEach(s => {
      const eau = parseFloat(s.eau) || 0
      const expected = (bestEau > 0 && eau > 0) ? best * Math.pow(bestEau / eau, VOLUME_EXP) : best
      if (s.unit_cost > expected) savings += s.spend * (1 - expected / s.unit_cost)
    })
    if (savings < minSize) return

    const worst = priced.reduce((a, b) => (a.unit_cost > b.unit_cost ? a : b))
    const sitesSorted = priced.slice().sort((a, b) => b.unit_cost - a.unit_cost)
    opps.push({
      type: 'site_gap',
      title: `${p.pn}${p.description ? ' (' + p.description + ')' : ''} — ${Math.round((rawSpread - 1) * 100)}% price gap across ${priced.length} sites`,
      parts: [p],
      sites: sitesSorted,
      spendExposed: p.total_spend,
      estSavings: savings,
      confidence: priced.length >= 4 ? 'High' : priced.length >= 3 ? 'Medium' : 'Low',
      detail: `${worst.site} pays €${worst.unit_cost.toFixed(2)}/unit while ${bestSite.site} achieves €${best.toFixed(2)} — a ${Math.round((rawSpread - 1) * 100)}% gap on the same part. Savings shown are volume-adjusted (only the portion beyond what order volume explains); pulling sites toward the best in-network price needs no requalification.`,
      vizData: { pn: p.pn, desc: p.description || '', best, bestSite: bestSite.site, spread: rawSpread, sites: sitesSorted },
    })
  })

  opps.sort((a, b) => b.estSavings - a.estSavings)

  // Compute achievability for each opportunity
  opps.forEach(opp => {
    opp.achievability = computeAchievability(opp, outlierThreshold)
  })

  return opps
}

/**
 * Achievability scoring (0–100) and label for an opportunity.
 * Factors:
 *   - Cohort quality (attribute count): 2 attrs = weaker benchmark, 3+ = solid
 *   - Cohort depth (members): more data points = more reliable
 *   - Existing cheaper alternative (quick_win = very actionable)
 *   - Site gap = highest actionability (same part, no requalification)
 *   - PP magnitude: extreme values (>5x) may signal data issues
 *   - Confidence already computed
 */
function computeAchievability(opp, outlierThreshold = 3) {
  let score = 50 // base

  if (opp.type === 'site_gap') {
    // Most actionable — same part, same supplier qualified, just redirect PO
    score = 90
    if (opp.confidence === 'High') score = 95
  } else if (opp.type === 'quick_win') {
    // Cheaper source already qualified — redirect PO
    score = 85
    // Outlier penalty: extreme ratios reduce achievability significantly
    // Based on real data: P75=2.8x, P90=6.7x, P95=13x
    if (opp.ppRatio > outlierThreshold * 3) score = 20 // almost certainly a data error
    else if (opp.ppRatio > outlierThreshold * 1.5) score = 35 // likely UoM mismatch
    else if (opp.ppRatio > outlierThreshold) score = 55 // unusual gap, needs validation
  } else if (opp.type === 'negotiation') {
    // Benchmark-based — quality depends on cohort
    score = 60
    // Cohort attribute count: average across parts
    const attrCounts = opp.parts.map(p => p.cohort_attr_count || 0).filter(c => c > 0)
    const avgAttrs = attrCounts.length > 0 ? attrCounts.reduce((s, c) => s + c, 0) / attrCounts.length : 0
    if (avgAttrs >= 4) score += 20
    else if (avgAttrs >= 3) score += 12
    else if (avgAttrs >= 2) score += 0 // stays at base
    // Cohort depth
    const cohortSizes = opp.parts.flatMap(p => p.suppliers.filter(s => s.cohort_size).map(s => s.cohort_size))
    const avgCohort = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0
    if (avgCohort >= 10) score += 8
    else if (avgCohort >= 5) score += 4
    // Number of affected line items (more = better leverage)
    if (opp.parts.length >= 5) score += 5
    // PP sanity: very extreme values suggest data quality issues
    if (opp.avgPP > 5) score -= 15
    else if (opp.avgPP > 3) score -= 5
  } else if (opp.type === 'sole_source') {
    // Hardest to execute — need new qualification
    score = 35
    if (opp.parts.length >= 5) score += 10
    if (opp.confidence === 'High') score += 10
  } else if (opp.type === 'part_family') {
    // Combined: price alignment + consolidation
    score = 55
    if (opp.parts.length >= 5) score += 10
    if (opp.confidence === 'High') score += 8
    // Price gap signal boosts score (measured savings vs estimated)
    if (opp.vizData?.hasPriceGap) score += 10
    // More suppliers = more consolidation opportunity
    if (opp.vizData?.supplierCount >= 6) score += 5
    // Outlier penalty for extreme spreads
    if (opp.ppRatio > outlierThreshold * 3) score = 25
    else if (opp.ppRatio > outlierThreshold * 1.5) score -= 15
    else if (opp.ppRatio > outlierThreshold) score -= 5
  }

  score = Math.max(10, Math.min(100, score))

  let label, color, tip
  if (score >= 80) {
    label = 'High'
    color = 'green'
    tip = 'Straightforward to execute — strong data support, clear action path'
  } else if (score >= 60) {
    label = 'Medium'
    color = 'amber'
    tip = 'Achievable with effort — benchmark is directional, validate with quotes'
  } else if (score >= 40) {
    label = 'Low'
    color = 'red'
    tip = 'Requires significant effort — coarse benchmark or new qualification needed'
  } else {
    label = 'Exploratory'
    color = 'gray'
    tip = 'Directional signal only — validate data before acting'
  }

  // Warnings for cohort-quality issues
  const warnings = []
  if (opp.type === 'negotiation') {
    const attrCounts = opp.parts.map(p => p.cohort_attr_count || 0).filter(c => c > 0)
    const avgAttrs = attrCounts.length > 0 ? attrCounts.reduce((s, c) => s + c, 0) / attrCounts.length : 0
    if (avgAttrs < 3) {
      warnings.push('Benchmark based on broad cohort (2 attributes) — less precise than narrow comparisons')
    }
    const cohortSizes = opp.parts.flatMap(p => p.suppliers.filter(s => s.cohort_size).map(s => s.cohort_size))
    const avgCohort = cohortSizes.length > 0 ? cohortSizes.reduce((s, c) => s + c, 0) / cohortSizes.length : 0
    if (avgCohort < 5) {
      warnings.push('Small cohort — fewer comparable transactions to benchmark against')
    }
    if (opp.avgPP && opp.avgPP > 5) {
      warnings.push('Extreme price deviation — verify unit/currency consistency before acting')
    }
  }
  if (opp.type === 'sole_source') {
    warnings.push('Requires qualifying a new supplier — lead time 3–12 months')
  }
  if (opp.type === 'part_family') {
    if (opp.vizData?.hasPriceGap) {
      warnings.push('Cross-PN price comparison — parts share engineering attributes but verify functional interchangeability')
    }
    if (opp.ppRatio > outlierThreshold * 1.5) {
      warnings.push('Very large spread (' + opp.ppRatio.toFixed(1) + 'x) — likely UoM or packaging difference between part numbers')
    }
    if (opp.vizData?.hasConsolidation && opp.vizData?.supplierCount >= 6) {
      warnings.push('High fragmentation (' + opp.vizData.supplierCount + ' suppliers) — consolidation requires procurement alignment across sites')
    }
  }
  // Outlier warnings for any opp type with extreme price ratios
  if (opp.outlierFlags && opp.outlierFlags.length > 0) {
    opp.outlierFlags.forEach(f => warnings.push(f))
  }
  if (opp.type === 'quick_win' || opp.type === 'site_gap') {
    // Check for extreme PP values even if ppRatio wasn't computed (site_gap)
    const allPPs = opp.parts.flatMap(p => p.suppliers.map(s => s.price_position).filter(Boolean))
    const maxPP = allPPs.length > 0 ? Math.max(...allPPs) : 0
    if (maxPP > outlierThreshold * 1.5 && !opp.outlierFlags?.length) {
      warnings.push('Price position >' + Math.round(maxPP) + 'x — possible data entry error (UoM, currency, or quantity basis mismatch)')
    }
  }

  return { score, label, color, tip, warnings }
}

/**
 * Compute deduplicated total savings across opportunities.
 * The same (pn, supplier) combo may appear in multiple opp types — we only
 * count the highest single savings claim per combo to avoid inflation.
 * For family-level opps (part_family), savings are distributed pro-rata.
 */
export function deduplicatedSavings(opps) {
  // Map: "pn||supplier" → max savings attributed to that key
  const keyMax = {}

  opps.forEach(opp => {
    if (opp.type === 'negotiation') {
      // Each item in vizData has specific per-part savings
      const items = opp.vizData?.items || []
      const totalOverspend = items.reduce((s, i) => s + (i.spend * (1 - 1 / i.pp)), 0) || opp.estSavings
      items.forEach(item => {
        const saving = item.spend * (1 - 1 / item.pp)
        const key = `${item.pn}||${opp.vizData?.supplierName || ''}`
        keyMax[key] = Math.max(keyMax[key] || 0, saving)
      })
      // If vizData items are truncated (slice(0,12)), add remaining proportionally
      if (items.length > 0 && opp.estSavings > totalOverspend * 1.01) {
        const residual = opp.estSavings - totalOverspend
        const rKey = `__residual_nego_${opp.vizData?.supplierName || ''}`
        keyMax[rKey] = Math.max(keyMax[rKey] || 0, residual)
      }
    } else if (opp.type === 'sole_source') {
      // Distributed across parts
      const parts = opp.parts || []
      parts.forEach(p => {
        const sup = p.suppliers?.[0]?.name || ''
        const share = parts.length > 0 ? (p.total_spend / (opp.spendExposed || 1)) * opp.estSavings : 0
        const key = `${p.pn}||${sup}`
        keyMax[key] = Math.max(keyMax[key] || 0, share)
      })
    } else if (opp.type === 'part_family') {
      // Pro-rata across all parts×suppliers in the family
      const parts = opp.parts || []
      parts.forEach(p => {
        (p.suppliers || []).forEach(s => {
          const share = (opp.spendExposed > 0) ? (s.spend / opp.spendExposed) * opp.estSavings : 0
          const key = `${p.pn}||${s.name}`
          keyMax[key] = Math.max(keyMax[key] || 0, share)
        })
      })
    } else if (opp.type === 'quick_win') {
      // Attributed to the expensive supplier on this part
      const pn = opp.vizData?.pn || opp.parts?.[0]?.pn || ''
      const sup = opp.vizData?.current?.name || ''
      const key = `${pn}||${sup}`
      keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings)
    } else if (opp.type === 'site_gap') {
      // Attributed to the part across all sites (supplier-agnostic)
      const pn = opp.vizData?.pn || opp.parts?.[0]?.pn || ''
      const key = `${pn}||__site_gap`
      keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings)
    } else {
      // Unknown type — count fully
      const key = `__other_${opp.title || ''}`
      keyMax[key] = Math.max(keyMax[key] || 0, opp.estSavings || 0)
    }
  })

  return Object.values(keyMax).reduce((s, v) => s + v, 0)
}

/**
 * Step-by-step negotiation playbook for an opportunity, tailored by type.
 * Returns { steps: string[] (may contain <strong>/<span class="step-data">) }.
 */
export function generateActionPlan(opp) {
  const d = opp.vizData || {}
    if (opp.type === 'negotiation') {
    const items = d.items || []
    const top3 = items.slice(0, 3)
    const highPP = items.filter(i => i.pp > 1.5)
    const steps = []

    // Strategy recommendation (if scored)
    if (opp.strategies && opp.strategies.length > 0) {
      const top = opp.strategies[0]
      const viable = opp.strategies.filter(s => s.score >= 50)
      const recList = viable.slice(1, 4).map(s => `${s.icon} <strong>${s.name}</strong> (${s.score}/100)`).join(' &nbsp;\u00B7&nbsp; ')
      const notRec = opp.strategies.filter(s => s.score < 30)
      const notRecStr = notRec.length > 0 ? ` Not recommended: ${notRec.map(s => s.name).join(', ')}.` : ''
      steps.push(`<strong>Recommended strategy:</strong> ${top.icon} <span class="step-data">${top.name}</span> (score ${top.score}/100). ${top.desc}${viable.length > 1 ? '<br/><span style="font-size:0.9em;color:#6b7280">Also viable: ' + recList + '</span>' : ''}${notRecStr ? '<br/><span style="font-size:0.85em;color:#9ca3af">' + notRecStr + '</span>' : ''}`)
    }

    steps.push(`<strong>Map the exposure:</strong> ${items.length} parts from <span class="step-data">${d.supplierName}</span> are priced above their cohort benchmark. Average position <span class="step-data">${(opp.avgPP || 0).toFixed(2)}x</span> (${Math.round(((opp.avgPP || 1) - 1) * 100)}% above market). Total spend at risk: ${formatEur(opp.spendExposed)}.`)
    if (top3.length > 0) {
      const topList = top3.map(i => `${i.desc ? i.pn + ' (' + i.desc + ')' : i.pn}: ${i.pp.toFixed(2)}x → ${formatEur(i.spend * (1 - 1 / i.pp))} overspend`).join('; ')
      steps.push(`<strong>Prioritize highest-overspend parts:</strong> ${topList}.`)
    }
    if (highPP.length > 0) {
      steps.push(`<strong>Flag critical outliers:</strong> ${highPP.length} part${highPP.length > 1 ? 's' : ''} exceed${highPP.length === 1 ? 's' : ''} 1.5x benchmark — these are pricing anomalies, not minor variances.`)
    }
    const goodItems = d.goodItems || []
    if (goodItems.length > 0) {
      const goodSpend = goodItems.reduce((s, g) => s + g.spend, 0)
      steps.push(`<strong>Acknowledge competitive items:</strong> ${goodItems.length} other parts from this supplier are priced at or below benchmark (<span class="step-data">${formatEur(goodSpend)}</span> competitive spend). Use this: "We recognize you're competitive on ${goodItems.length} items — we just need the same discipline on these ${items.length} outliers."`)
    }
    const topItem = top3[0]
    const targetUC = topItem?.bm ? (topItem.bm * 1.1).toFixed(2) : null
    steps.push(`<strong>Set your target:</strong> Aim for <span class="step-data">1.1x benchmark</span> (10% premium max)${topItem ? `. Example: ${topItem.desc ? topItem.pn + ' (' + topItem.desc + ')' : topItem.pn} target €${targetUC} vs current €${topItem.uc.toFixed(2)}` : ''}.`)
    steps.push(`<strong>Challenge the supplier:</strong> "Our benchmarking shows ${items.length} line items priced ${Math.round(((opp.avgPP || 1) - 1) * 100)}% above comparable parts. We have data on each one. Please review and provide revised pricing within 2 weeks."`)
    steps.push(`<strong>Leverage total volume:</strong> Bundle all ${items.length} affected parts — total spend <span class="step-data">${formatEur(opp.spendExposed)}</span>. "If you align pricing, we commit volume. If not, we issue RFQs to qualified alternates."`)
    steps.push(`<strong>Walk-away position:</strong> Anything above <span class="step-data">1.3x benchmark</span> is non-negotiable. Set 2-week deadline, then competitive RFQ.`)
    return steps
  }
  if (opp.type === 'quick_win') {
    const savePct = Math.round((1 - d.alternative.pp / d.current.pp) * 100)
    const qwLabel = d.desc ? `${d.pn} (${d.desc})` : d.pn
    return [
      `<strong>Verify qualification:</strong> Confirm ${d.alternative.name} is already approved for part ${qwLabel}.`,
      `<strong>Get a fresh quote:</strong> Request updated pricing from ${d.alternative.name} — historical position <span class="step-data">${d.alternative.pp.toFixed(2)}x benchmark</span>.`,
      `<strong>Present to incumbent:</strong> Show ${d.current.name} the competitive quote. "We have a qualified alternative at ${savePct}% less — can you match?"`,
      `<strong>Decision gate:</strong> If ${d.current.name} matches → stay (saves switching cost). If not → execute the switch.`,
      `<strong>Switch if needed:</strong> Redirect the PO to ${d.alternative.name}. Potential annual savings: <span class="step-data">${formatRange(opp.estSavings)}</span>.`,
    ]
  }
  if (opp.type === 'sole_source') {
    const topParts = (d.topParts || []).slice(0, 3).map(p => p.desc ? `${p.pn} (${p.desc})` : p.pn).join(', ')
    const avgSpend = d.partCount ? formatEur(d.totalSpend / d.partCount) : '—'
    return [
      `<strong>Identify alternates:</strong> Research 2–3 suppliers for your top parts (${topParts}). Start with suppliers already in your network for adjacent families.`,
      `<strong>Issue exploratory RFQ:</strong> Request budgetary quotes for the highest-spend parts — just price signals, no full qualification yet.`,
      `<strong>Use as leverage:</strong> Share budgetary quotes with ${d.supplierName}: "We're evaluating options for risk mitigation. Can you sharpen pricing to justify staying single-source?"`,
      `<strong>Negotiate a volume commitment:</strong> If no viable alternates, negotiate a 2–3 year agreement with ${d.supplierName} for an 8–12% reduction.`,
      `<strong>Invest in qualification:</strong> For parts over <span class="step-data">${avgSpend}</span> annual spend, qualify a second source. Payback typically < 18 months.`,
      `<strong>Set a dual-source target:</strong> At least 2 qualified suppliers for all parts above €10K annual spend within 12 months.`,
    ]
  }
  if (opp.type === 'part_family') {
    const top2 = (d.suppliers || []).slice(0, 2).map(s => s.name).join(' and ')
    const partPrices = d.partPrices || []
    const bestUC = d.best?.uc || 0
    // Identify top 3 parts by savings potential (gap vs best-in-family)
    const ranked = partPrices.filter(p => p.uc > bestUC).map(p => ({ ...p, gap: p.uc - bestUC, savingsPot: p.spend * (1 - bestUC / p.uc) })).sort((a, b) => b.savingsPot - a.savingsPot)
    const top3 = ranked.slice(0, 3)

    // Compute similarity scores vs the reference (best-priced) part
    const refPart = (opp.parts || []).find(p => p.pn === d.best?.pn)
    let highSimCount = 0, lowSimCount = 0
    if (refPart) {
      (opp.parts || []).forEach(p => {
        if (p === refPart) return
        const sim = computeSimilarity(refPart, p)
        if (sim.pct >= 70) highSimCount++
        else if (sim.pct < 40) lowSimCount++
      })
    }

    const steps = []
    steps.push(`<strong>Map the family:</strong> ${d.partCount} engineering-similar parts across ${d.supplierCount} suppliers \u2014 total spend <span class="step-data">${formatEur(d.totalSpend)}</span>. Prices range \u20ac${bestUC.toFixed(2)}\u2013\u20ac${(d.worst?.uc || 0).toFixed(2)} (${Math.round(d.spread)}\u00d7).`)
    if (d.hasPriceGap) {
      const bestLabel = d.best?.desc ? `${d.best.pn} (${d.best.desc})` : d.best?.pn
      let simSummary = ''
      if (highSimCount > 0) simSummary = ` Similarity analysis: <span class="step-data">${highSimCount} parts are \u226570% similar</span> to the reference \u2014 price differences are likely negotiable.`
      if (lowSimCount > 0) simSummary += ` ${lowSimCount} part${lowSimCount > 1 ? 's' : ''} score below 40% \u2014 may have legitimate engineering differences.`
      steps.push(`<strong>Verify engineering equivalence:</strong> Compare each part\u2019s specs to the best-priced reference (${bestLabel}). Focus on material, process, tolerance, and size.${simSummary} See the Data Pack for the full side-by-side comparison with similarity scores.`)
      steps.push(`<strong>Set the target price:</strong> Best-in-family is <span class="step-data">${bestLabel} at \u20ac${bestUC.toFixed(2)}</span>. Use this as the anchor. For parts with higher EAU, expect even lower pricing. For lower EAU, allow up to +40% volume adjustment.`)
      if (top3.length > 0) {
        const topList = top3.map(p => `${p.desc ? p.pn + ' (' + p.desc + ')' : p.pn}: \u20ac${p.uc.toFixed(2)} \u2192 target \u20ac${bestUC.toFixed(2)} (${formatEur(p.savingsPot)} potential)`).join('; ')
        steps.push(`<strong>Prioritize high-impact parts:</strong> Start with these: ${topList}.`)
      }
      steps.push(`<strong>Challenge suppliers:</strong> "We buy ${d.partCount} similar parts in the same process family. Our best price is \u20ac${bestUC.toFixed(2)}. Please explain why this part is ${Math.round(d.spread)}\u00d7 higher, or align pricing within 2 weeks."`)
    }
    if (d.hasConsolidation) {
      steps.push(`<strong>Bundle the RFQ:</strong> Consolidate all ${d.partCount} parts into a single RFQ package. Present full family volume (<span class="step-data">${formatEur(d.totalSpend)}</span>) to ${top2}. Ask for family-rate pricing.`)
      steps.push(`<strong>Negotiate volume tiers:</strong> "If we consolidate the full ${formatEur(d.totalSpend)} with you, what family discount can you offer?" Target 8\u201315% improvement on volume alone.`)
    }
    steps.push(`<strong>Set timeline & escalation:</strong> Request revised pricing within 2 weeks. If no movement, issue competitive RFQ to 2\u20133 alternates for the full family.`)
    return steps
  }
  if (opp.type === 'site_gap') {
    const sites = d.sites || []
    const worst = sites[0]
    const sgLabel = d.desc ? `${d.pn} (${d.desc})` : (opp.parts[0]?.description ? `${opp.parts[0].pn} (${opp.parts[0].description})` : opp.parts[0]?.pn)
    return [
      `<strong>Confirm it's the same part:</strong> ${sgLabel} is purchased at ${sites.length} sites — verify identical spec and revision across ${sites.map(s => s.site).slice(0, 4).join(', ')}.`,
      `<strong>Establish the benchmark:</strong> Best in-network price is <span class="step-data">${formatEur(d.best || 0)}/unit</span> at ${d.bestSite}. That's your internal target — already achieved, no negotiation needed to prove it.`,
      `<strong>Tackle the worst gap first:</strong> ${worst ? `${worst.site} pays €${worst.unit_cost.toFixed(2)} (+${Math.round((worst.unit_cost / d.best - 1) * 100)}%)` : ''}. Greatest savings per unit of effort.`,
      `<strong>Harmonize contracts:</strong> Extend the best site's price/terms network-wide, or consolidate the volume of all ${sites.length} sites under one agreement.`,
      `<strong>Target outcome:</strong> Pull every site to best-in-network — <span class="step-data">${formatRange(opp.estSavings)}</span> annual savings with no requalification.`,
    ]
  }
  return []
}

/**
 * Generate expanded context data for each action plan step.
 * Returns an array of expansion objects (one per step). Each has:
 *   { tables: [{title, headers, rows}], bullets: string[], emailDraft: string|null, drawings: [{pn, fileId, filename}] }
 * Only populated for negotiation type currently.
 */
export function generateExpandedStepData(opp) {
  if (opp.type !== 'negotiation') return []
  const d = opp.vizData || {}
  const items = d.items || []
  const goodItems = d.goodItems || []
  const parts = opp.parts || []
  const supplierName = d.supplierName || ''
  const expansions = []

  // Step 0: Strategy recommendation
  if (opp.strategies && opp.strategies.length > 0) {
    const strat = opp.strategies
    const topStrat = strat[0]
    expansions.push({
      tables: [{
        title: 'Strategy Score Breakdown',
        headers: ['Strategy', 'Score', 'Fit'],
        rows: strat.map(s => [
          `${s.icon} ${s.name}`,
          `${s.score}/100`,
          s.score >= 70 ? 'Recommended' : s.score >= 50 ? 'Viable' : s.score >= 30 ? 'Consider' : 'Not recommended',
        ]),
      }, {
        title: `Score Factors: ${topStrat.icon} ${topStrat.name} (${topStrat.score}/100)`,
        headers: ['Signal', 'Points'],
        rows: (topStrat.factors || []).map(f => [f.signal, f.pts > 0 ? `+${f.pts}` : String(f.pts)]),
      }, ...(strat[1] && strat[1].score >= 50 ? [{
        title: `Score Factors: ${strat[1].icon} ${strat[1].name} (${strat[1].score}/100)`,
        headers: ['Signal', 'Points'],
        rows: (strat[1].factors || []).map(f => [f.signal, f.pts > 0 ? `+${f.pts}` : String(f.pts)]),
      }] : [])],
      bullets: [
        `Primary strategy: ${topStrat.name} — ${topStrat.desc}`,
        ...strat.filter(s => s.score >= 50 && s !== strat[0]).map(s => `Alternative: ${s.name} (${s.score}/100) — ${s.desc}`),
        ...strat.filter(s => s.score < 30).map(s => `Not recommended: ${s.name} — insufficient data signals or poor fit for this supplier profile.`),
      ],
      emailDraft: null,
      drawings: [],
    })
  }

  // Step 1: Map the exposure
  const totalGoodSpend = goodItems.reduce((s, g) => s + g.spend, 0)
  const totalRelationship = opp.spendExposed + totalGoodSpend
  expansions.push({
    tables: [{
      title: 'Full Supplier Relationship',
      headers: ['Category', 'Parts', 'Spend', '% of Total'],
      rows: [
        ['Overpriced (>1.3x)', String(items.length), formatEur(opp.spendExposed), `${Math.round(opp.spendExposed / totalRelationship * 100)}%`],
        ['Competitive (\u22641.3x)', String(goodItems.length), formatEur(totalGoodSpend), `${Math.round(totalGoodSpend / totalRelationship * 100)}%`],
        ['Total relationship', String(items.length + goodItems.length), formatEur(totalRelationship), '100%'],
      ],
    }, {
      title: 'Overpriced Parts Detail',
      headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'EAU', 'Spend', 'Overspend'],
      rows: items.map(i => [
        i.pn, i.desc || '\u2014',
        `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
        `${i.pp.toFixed(2)}x`, i.eau > 0 ? Math.round(i.eau).toLocaleString() : '\u2014',
        formatEur(i.spend), formatEur(i.spend * (1 - 1 / i.pp)),
      ]),
    }],
    bullets: [
      `Total annual business with ${supplierName}: ${formatEur(totalRelationship)} (${items.length + goodItems.length} line items).`,
      `${Math.round(opp.spendExposed / totalRelationship * 100)}% of spend is above benchmark — this is the negotiation target.`,
      goodItems.length > 0 ? `${goodItems.length} parts are priced competitively — proof this supplier CAN deliver fair pricing.` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: items.map(i => {
      const p = parts.find(pt => pt.pn === i.pn)
      return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
    }).filter(Boolean),
  })

  // Step 2: Prioritize highest-overspend
  const top5 = items.slice(0, 5)
  expansions.push({
    tables: [{
      title: 'Top Parts by Overspend',
      headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'Overspend', 'Target (1.1x)', 'Savings @ Target'],
      rows: top5.map(i => {
        const target = i.bm > 0 ? i.bm * 1.1 : null
        const savings = target ? i.spend * (1 - target / i.uc) : 0
        return [
          i.pn, i.desc || '\u2014',
          `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
          `${i.pp.toFixed(2)}x`, formatEur(i.spend * (1 - 1 / i.pp)),
          target ? `\u20AC${target.toFixed(2)}` : '\u2014', formatEur(savings),
        ]
      }),
    }, {
      title: 'Benchmark Methodology',
      headers: ['Part #', 'Description', 'Cohort', 'Cohort Size', 'Median UC', 'Volume Adj.'],
      rows: top5.map(i => {
        const p = parts.find(pt => pt.pn === i.pn)
        const sup = p?.suppliers?.find(s => s.name === supplierName)
        return [
          i.pn,
          i.desc || p?.description || '\u2014',
          humanizeCohortName(sup?.cohort_name),
          String(sup?.cohort_size || '\u2014'),
          sup?.cohort_median_uc ? `\u20AC${sup.cohort_median_uc.toFixed(2)}` : '\u2014',
          sup?.cohort_median_eau ? `EAU ${Math.round(sup.cohort_median_eau).toLocaleString()}` : '\u2014',
        ]
      }),
    }],
    bullets: [
      `Benchmark formula: Median UC \u00D7 (Median EAU / Part EAU)^0.4 — adjusts for volume differences.`,
      `Each cohort groups parts by: manufacturing method, material, size, tolerance, and complexity.`,
      top5[0]?.bm > 0 ? `Example: ${top5[0].pn} (${top5[0].desc || '\u2014'}) benchmark \u20AC${top5[0].bm.toFixed(2)} based on ${parts.find(pt => pt.pn === top5[0].pn)?.suppliers?.find(s => s.name === supplierName)?.cohort_size || '?'}-member cohort.` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: top5.map(i => {
      const p = parts.find(pt => pt.pn === i.pn)
      return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
    }).filter(Boolean),
  })

  // Step 3: Flag critical outliers (only if any)
  const highPP = items.filter(i => i.pp > 1.5)
  if (highPP.length > 0) {
    expansions.push({
      tables: [{
        title: 'Critical Outliers (>1.5x benchmark)',
        headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'Cohort', 'Cohort Size'],
        rows: highPP.map(i => {
          const p = parts.find(pt => pt.pn === i.pn)
          const sup = p?.suppliers?.find(s => s.name === supplierName)
          return [
            i.pn, i.desc || '\u2014',
            `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
            `${i.pp.toFixed(2)}x`,
            humanizeCohortName(sup?.cohort_name),
            String(sup?.cohort_size || '\u2014'),
          ]
        }),
      }, {
        title: 'Cohort Peers (Proof of Market Rate)',
        headers: ['Part #', 'Description', 'Supplier', 'Unit Cost', 'EAU'],
        rows: highPP.slice(0, 3).flatMap(i => {
          const p = parts.find(pt => pt.pn === i.pn)
          const sup = p?.suppliers?.find(s => s.name === supplierName)
          return (sup?.cohort_entries || []).filter(e => e.supplier !== supplierName).slice(0, 3).map(e => [
            e.pn || '\u2014', e.description || '\u2014', e.supplier || '\u2014',
            `\u20AC${e.unit_cost.toFixed(2)}`, e.eau > 0 ? Math.round(e.eau).toLocaleString() : '\u2014',
          ])
        }),
      }],
      bullets: (() => {
        const topOutlier = highPP[0]
        const topPart = parts.find(pt => pt.pn === topOutlier.pn)
        const topSup = topPart?.suppliers?.find(s => s.name === supplierName)
        const cohortLabel = humanizeCohortName(topSup?.cohort_name) || 'unknown'
        const cohortSize = topSup?.cohort_size || '?'
        return [
          `${highPP.length} part${highPP.length > 1 ? 's' : ''} exceed 1.5x — these are not minor variances. They require specific justification from the supplier.`,
          `Question to ask: "What specific engineering requirements justify ${Math.round((topOutlier.pp - 1) * 100)}% above comparable parts made by other suppliers in the [${cohortLabel}] cohort (${cohortSize} members)?"`,
          `If no valid justification: these are pricing anomalies, not cost-driven premiums.`,
        ]
      })(),
      emailDraft: null,
      drawings: highPP.map(i => {
        const p = parts.find(pt => pt.pn === i.pn)
        return p ? { pn: i.pn, fileId: p.file_id || null, filename: p.filename || '' } : null
      }).filter(Boolean),
    })
  }

  // Step 4: Acknowledge competitive items (only if any)
  if (goodItems.length > 0) {
    expansions.push({
      tables: [{
        title: 'Competitive Items from Same Supplier',
        headers: ['Part #', 'Description', 'UC', 'Benchmark', 'PP', 'EAU', 'Spend'],
        rows: goodItems.map(g => [
          g.pn, g.desc || '\u2014',
          `\u20AC${g.uc.toFixed(2)}`, g.bm > 0 ? `\u20AC${g.bm.toFixed(2)}` : '\u2014',
          `${g.pp.toFixed(2)}x`, g.eau > 0 ? Math.round(g.eau).toLocaleString() : '\u2014',
          formatEur(g.spend),
        ]),
      }, {
        title: 'Attribute Comparison: Competitive vs. Overpriced',
        headers: ['Attribute', 'Competitive Items', 'Overpriced Items'],
        rows: (() => {
          const compMats = [...new Set(goodItems.map(g => g.material || '').filter(Boolean))]
          const overMats = [...new Set(items.map(i => { const p = parts.find(pt => pt.pn === i.pn); return p?.material || '' }).filter(Boolean))]
          const compMethods = [...new Set(goodItems.map(g => g.method || '').filter(Boolean))]
          const overMethods = [...new Set(items.map(i => { const p = parts.find(pt => pt.pn === i.pn); return p?.method || '' }).filter(Boolean))]
          return [
            ['Materials', compMats.join(', ') || '\u2014', overMats.join(', ') || '\u2014'],
            ['Methods', compMethods.join(', ') || '\u2014', overMethods.join(', ') || '\u2014'],
            ['Avg PP', `${(goodItems.reduce((s, g) => s + g.pp, 0) / goodItems.length).toFixed(2)}x`, `${(items.reduce((s, i) => s + i.pp, 0) / items.length).toFixed(2)}x`],
            ['Total Spend', formatEur(totalGoodSpend), formatEur(opp.spendExposed)],
          ]
        })(),
      }],
      bullets: [
        `${goodItems.length} parts at/below benchmark (\u20AC${totalGoodSpend.toLocaleString()} spend) — supplier IS competitive on these.`,
        `Key talking point: "You price ${goodItems.length} items competitively using the same processes. What justifies the premium on these ${items.length} outliers?"`,
        `If same material and method: there is no valid cost justification for the gap.`,
      ],
      emailDraft: null,
      drawings: [],
    })
  }

  // Step 5: Set your target
  expansions.push({
    tables: [{
      title: 'Target Price Table',
      headers: ['Part #', 'Description', 'Current UC', 'Benchmark', 'Target (1.1x)', 'Savings/Unit', 'EAU', 'Annual Savings'],
      rows: items.map(i => {
        const target = i.bm > 0 ? i.bm * 1.1 : null
        const savingsUnit = target ? i.uc - target : 0
        const annSavings = target && i.eau > 0 ? savingsUnit * i.eau : (target ? i.spend * (1 - target / i.uc) : 0)
        return [
          i.pn, i.desc || '\u2014',
          `\u20AC${i.uc.toFixed(2)}`, i.bm > 0 ? `\u20AC${i.bm.toFixed(2)}` : '\u2014',
          target ? `\u20AC${target.toFixed(2)}` : '\u2014',
          target ? `\u20AC${savingsUnit.toFixed(2)}` : '\u2014',
          i.eau > 0 ? Math.round(i.eau).toLocaleString() : '\u2014',
          formatEur(annSavings),
        ]
      }),
    }, {
      title: 'Sensitivity Analysis',
      headers: ['Target Level', 'Premium over BM', 'Total Savings', 'Savings %'],
      rows: [1.0, 1.05, 1.1, 1.15, 1.2, 1.3].map(mult => {
        const totalSav = items.reduce((s, i) => {
          if (!i.bm || i.bm <= 0) return s
          const target = i.bm * mult
          if (target >= i.uc) return s
          return s + i.spend * (1 - target / i.uc)
        }, 0)
        return [
          `${mult.toFixed(2)}x`, `+${Math.round((mult - 1) * 100)}%`,
          formatEur(totalSav), `${Math.round(totalSav / opp.spendExposed * 100)}% of spend`,
        ]
      }),
    }],
    bullets: [
      `Target: 1.1x benchmark = 10% premium to account for service/quality/relationship factors.`,
      `Total potential at 1.1x target: ${formatEur(items.reduce((s, i) => i.bm > 0 ? s + i.spend * (1 - (i.bm * 1.1) / i.uc) : s, 0))}.`,
      `Floor reference: cheapest cohort peer sets the absolute floor. 1.1x is a fair, defensible ask.`,
    ],
    emailDraft: null,
    drawings: [],
  })

  // Step 6: Challenge the supplier (email draft)
  const totalOverspend = items.reduce((s, i) => s + i.spend * (1 - 1 / i.pp), 0)
  expansions.push({
    tables: [],
    bullets: [
      `Tone: Collaborative but firm. Lead with data, not demands.`,
      `Attach: The price analysis table from this data pack.`,
      `Request: Revised pricing within 2 weeks (14 calendar days).`,
      `Escalation: If no response within 2 weeks, schedule a QBR. If no movement after QBR, proceed to competitive sourcing.`,
    ],
    emailDraft: `Subject: Pricing Review Request \u2014 ${items.length} Line Items\n\nDear ${supplierName} Team,\n\nAs part of our ongoing supply chain optimization, we have completed a benchmarking analysis of our portfolio with your company.\n\nOur analysis shows ${items.length} parts priced ${Math.round(((opp.avgPP || 1) - 1) * 100)}% above comparable market rates (${formatEur(opp.spendExposed)} annual spend). We have detailed benchmarking data for each line item and would like to discuss alignment.\n\n${goodItems.length > 0 ? `We note that ${goodItems.length} other items in our portfolio with you are priced competitively \u2014 we value the relationship and want to resolve this collaboratively.\n\n` : ''}We propose:\n1. Review the attached price position analysis\n2. Provide revised pricing for highlighted items within 2 weeks\n3. Schedule a brief call to discuss any items requiring clarification\n\nOur target is ${formatEur(items.reduce((s, i) => i.bm > 0 ? s + i.spend * (1 - (i.bm * 1.1) / i.uc) : s, 0))} annual savings, which we believe is achievable while maintaining fair margins on your side.\n\nPlease let us know your availability for a brief discussion.\n\nBest regards`,
    drawings: [],
  })

  // Step 7: Leverage total volume
  expansions.push({
    tables: [{
      title: 'Volume Commitment Package',
      headers: ['Item', 'Value'],
      rows: [
        ['Overpriced parts (negotiation target)', `${items.length} parts, ${formatEur(opp.spendExposed)}`],
        ['Competitive parts (retention commitment)', `${goodItems.length} parts, ${formatEur(totalGoodSpend)}`],
        ['Total volume at stake', formatEur(totalRelationship)],
        ['Your ask', 'Align overpriced parts to 1.1x benchmark'],
        ['Your offer', `Commit ${formatEur(totalRelationship)} annual volume for 2\u20133 year term`],
      ],
    }],
    bullets: [
      `Bundle message: "We're not just asking for a price cut \u2014 we're offering volume certainty in exchange for fair pricing."`,
      `If ${items.length} parts align: commit the full ${formatEur(totalRelationship)} portfolio.`,
      `If they refuse: the ${formatEur(opp.spendExposed)} in overpriced spend goes to RFQ within 30 days.`,
      goodItems.length > 0 ? `Reminder: "${goodItems.length} parts prove you can be competitive. Extend that discipline and you keep everything."` : null,
    ].filter(Boolean),
    emailDraft: null,
    drawings: [],
  })

  // Step 8: Walk-away position
  const altSuppliers = new Set()
  items.forEach(i => {
    const p = parts.find(pt => pt.pn === i.pn)
    const sup = p?.suppliers?.find(s => s.name === supplierName)
    ;(sup?.cohort_entries || []).forEach(e => {
      if (e.supplier && e.supplier !== supplierName) altSuppliers.add(e.supplier)
    })
  })
  expansions.push({
    tables: [{
      title: 'Alternative Suppliers (from cohort data)',
      headers: ['Supplier', 'Seen in Cohorts'],
      rows: [...altSuppliers].slice(0, 10).map(name => [name, 'Yes']),
    }],
    bullets: [
      `${altSuppliers.size} alternative supplier${altSuppliers.size !== 1 ? 's' : ''} identified from cohort benchmarking data.`,
      `Walk-away threshold: 1.3x benchmark. Anything above this after 2-week deadline triggers competitive RFQ.`,
      `RFQ scope: ${items.length} parts, ${formatEur(opp.spendExposed)} annual volume.`,
      `Timeline: 2-week response deadline \u2192 2-week RFQ \u2192 4-week qualification = parts can move in ~8 weeks.`,
    ],
    emailDraft: null,
    drawings: [],
  })

  return expansions
}
